import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Logo } from '../components/Logo'
import { ErrorBox, Field } from '../components/ui'
import { ApiError, errorMessage } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'

export function Login() {
  const { login, loginOperator, providers } = useAuth()
  const { t, lang, setLang } = useT()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tenant, setTenant] = useState('')
  const [needTenant, setNeedTenant] = useState(false)
  const [mode, setMode] = useState<'user' | 'operator'>('user')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'operator') await loginOperator(email, password)
      else await login(email, password, tenant || undefined)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'tenant_required') {
        setNeedTenant(true)
        setError(t('This email exists in more than one tenant. Add the tenant handle.'))
      } else setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-full">
      <div className="hidden w-[46%] flex-col justify-between bg-sky-500 p-14 text-white lg:flex" style={{ color: 'rgb(var(--accent-contrast))' }}>
        <Link to="/" className="inline-flex">
          <Logo size={40} onDark wordSize={20} />
        </Link>
        <div>
          <h1 className="serif max-w-md text-[42px] font-semibold leading-[1.15]">{t('Price governance for sales teams.')}</h1>
        </div>
        <div className="text-[13px] opacity-60">pricepilot.cognivance.org</div>
      </div>

      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-[400px]">
          <div className="mb-6 flex items-center justify-between">
            <Link to="/" className="text-[13px] text-slate-500 hover:text-slate-200">
              ← pricepilot.cognivance.org
            </Link>
            <button type="button" className="text-[13px] text-slate-500 hover:text-slate-200" onClick={() => setLang(lang === 'de' ? 'en' : 'de')}>
              {lang === 'de' ? 'English' : 'Deutsch'}
            </button>
          </div>
          <div className="mb-8">
            <h2 className="serif text-[28px] font-bold text-sky-500">{mode === 'operator' ? t('Platform sign-in') : t('Sign in')}</h2>

          </div>

          {providers.oidc && mode === 'user' && (
            <a href="/api/auth/oidc/login" className="btn btn-primary mb-6 w-full">
              {t('Continue with Microsoft Entra ID')}
            </a>
          )}

          {(providers.local || mode === 'operator') && (
            <form onSubmit={submit} className="space-y-4">
              {providers.oidc && mode === 'user' && (
                <div className="flex items-center gap-3 text-[12px] text-slate-500">
                  <span className="h-px flex-1 bg-slate-800" />
                  {t('or with a password')}
                  <span className="h-px flex-1 bg-slate-800" />
                </div>
              )}
              <Field label={t('Email')}>
                <input className="field" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </Field>
              <Field label={t('Password')}>
                <input className="field" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </Field>
              {mode === 'user' && (needTenant || tenant) && (
                <Field label={t('Tenant handle')} hint={t('For example: demo-industries')}>
                  <input className="field font-mono" value={tenant} onChange={(e) => setTenant(e.target.value)} autoFocus />
                </Field>
              )}
              {error && <ErrorBox>{error}</ErrorBox>}
              <button type="submit" className="btn btn-primary w-full" disabled={busy}>
                {busy ? t('Signing in…') : t('Sign in')}
              </button>
            </form>
          )}

          <button
            type="button"
            className="mt-8 w-full text-center text-[13px] text-slate-500 hover:text-slate-200"
            onClick={() => {
              setMode(mode === 'user' ? 'operator' : 'user')
              setError(null)
            }}
          >
            {mode === 'user' ? t('Platform operator? Sign in here') : t('Back to tenant sign-in')}
          </button>

          {mode === 'user' && providers.local && ['localhost', '127.0.0.1'].includes(window.location.hostname) && (
            <div className="mt-10 rounded-md border border-dashed border-slate-800 p-4 text-[12px] leading-relaxed text-slate-500">
              <div className="label mb-1">Demo accounts · password “password”</div>
              rep.at-north.1@demo.test · sup.at-north@demo.test · head@demo.test · admin@demo.test
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
